import { OwaPalette } from 'owa-theme-shared';
import { OwaDate } from 'owa-datetime';
import { DataProviderOpxResponse } from 'owa-data-provider-opx-contract';
import { ActivityFeedAction } from 'owa-activity-feed-opx-contract';
import { OpxSessionInfo } from 'owa-config';

export interface OpxHostCoreApi {
    // OPX FRAMEWORK APIs //

    /** Retrieve the auth token from the host app, or else `undefined` to fall back on cookie auth (not recommended) */
    getAuthToken(resource: string): Promise<string | undefined>;

    /** Get a custom color palette from the host app for theming, or else `undefined` to fall back to OWA base theme */
    getOpxPalette: (isDarkTheme?: boolean) => Promise<OwaPalette | undefined>;

    /** Get session information from the host app for telemetry purposes */
    getSessionInfo(): Promise<OpxSessionInfo>;

    /** Check whether the host app has enabled dark theme, or else `false` / `undefined` to fall back to standard theming */
    isDarkTheme: () => Promise<boolean | undefined>;

    /** Notify the host app that a fatal error has occurred */
    onFatalError: () => void;

    /** Notify the host app that the OPX component has removed first OWA loader and starts drawing/mounting UI elements */
    onLoaderRemoved: () => void;

    /** Notify the host app to refresh the auth token */
    refreshAuthToken: () => void;

    /** Lifecycle signal from the OPX component to get a new token for unauthorized request error */
    updateAuthTokenUsingClaimsChallenge: (claimsChallenge: string) => void;

    /** Register a callback to execute when the host app changes theme */
    registerThemeChangedHandler?: (callback: (isDarkTheme: boolean) => void) => void;

    // OPX COMPONENT APIs //

    /** Get scenario-specific data from the host app when the OPX component is first initialized */
    getScenarioData: <T = never>() => Promise<T>;

    /** Notify the host app that the OPX component has finished mounting */
    onPageReady: () => void;

    /** Notify the host app that the OPX component has completed its user scenario and can be closed/unmounted/hidden */
    onOwaTaskCompleted(result?: string);

    /** Register a callback to execute when the host app updates scenario-specific data */
    registerScenarioDataUpdatedHandler: <T = never>(callback: (value: T) => void) => void;

    /** Register a callback to execute when the host app wants to switch to a different component */
    registerNewRouteHandler?: (callback: (route: string) => void) => void;
}

export interface OpxHostApi extends OpxHostCoreApi {
    // SCENARIO-SPECIFIC OPX COMPONENT APIs //

    onGroupCreated: (groupSmtpAddress: string) => void;
    onMeetingInsightsContainerHeightUpdated: (height: number) => void;
    onMeetingInsightsCountUpdated: (count: number) => void;
    onRoomAdded: (roomEmail: string) => void;
    processSearchAnswersCallback: (callbackName: string, data: {}) => void;
    processMeetingInsightsCallback: (
        callbackName: string,
        data: {},
        defaultCallback: CallbackType
    ) => void;
    updateMeetingInsightsCallbackRegistration: (callbackRegistration: string[]) => void;
    onDataProviderResponse: (response: DataProviderOpxResponse) => void;
    onActivityFeedAction: (action: ActivityFeedAction) => void;

    // NOT YET IMPLEMENTED IN DEFAULT ADAPTOR (MIX OF FRAMEWORK, COMPONENT, AND SCENARIO APIs) //

    /** Get the custom color to assign to the user's default calendar */
    getDefaultCalendarColor?: () => string;

    /** Check whether a given feature has been disabled for the OPX session by the host app */
    isFeatureDisabled: (feature: Feature) => boolean;

    /** Handle joining a Teams meeting */
    joinTeamsMeeting?: (event: OpxCalendarEvent) => void;

    /** Notify the host app that the OPX component has removed scenario specific loader (spinner) */
    onScenarioLoaderRemoved: () => void;

    modalOpened?: (isOpened: boolean) => void;
    navigateToView?: (view: OpxView, action?: OpxAction, event?: any) => void;
    registerEditButtonHandler?: (callback: CallbackType) => void;
    registerFormSaveHandler?: (callback: CallbackType) => void;
    registerFormCloseHandler?: (callback: CallbackType) => void;
    registerNewButtonHandler?: (callback: CallbackType) => void;
    sendPLTDataPoint?: (view: OpxView, timeTakenInMs?: number) => void;
}

export type CallbackType = () => void;
export type OpxView = 'ComposeForm' | 'EditForm' | 'Surface' | 'DetailsForm';
export type OpxAction = 'Save' | 'Send' | 'Open' | 'Edit';

/** List of OWA features that can be conditionally disabled through the OPX host API */
export enum Feature {
    AddIns,
    ComposeFormHeader,
    DiscardInFullCompose,
    SaveInFullCompose,
    SchedulingAssistant,
    Settings,
    MultiAccounts,
}

/**
 * This is the CalendarEvent interface that OPX scenarios use.
 * It contains all the properties OPX scenarios need to work.
 * This will be a subset of CalendarEvent interface
 */
export interface OpxCalendarEvent {
    Subject: string;
    Start: OwaDate;
    End: OwaDate;
    IsRecurring: boolean;
    OnlineMeetingJoinUrl: string;
    JoinOnlineMeetingUrl: string;
    SkypeTeamsProperties: OpxSkypeTeamsPropertiesData;
}

/**
 * The SkypeTeamsProperties object needed when an event is created on a channel
 */
export interface OpxSkypeTeamsPropertiesData {
    cid: string;
    rid: number;
    mid: number;
    uid: string;
    private: boolean;
    type: number;
}
